import { PaymentMethod } from '@stripe/stripe-js';
import Uri from 'jsuri';
import { isEmpty, isNil } from 'lodash';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { GENERAL_ERROR_MSG } from '../../constants/stripe';
import { SetupIntentError, SetupIntentResult } from '../../stripe-payment/stripe-payment-element';
import { ErrorCodes, OfferType, PackageId } from '../../types/billing';
import { checkResponse, extractErrorMessage } from '../../utils/billing';
import { SharedBillingState } from '../defaults/shared-billing';
import {
  NOTICE_ERROR_TYPE,
  SUBSCRIPTION_CODE_NOTICE_DURATION_MS,
  showFlashNotice,
} from './notices';

export const SECURE_BASE_URL = typeof window === 'undefined' ? '' : window.location.origin;
export const VALIDATE_SUBSCRIPTION_CODE_URL = `${SECURE_BASE_URL}/billing/offer_details`;
export const SETUP_STRIPE_INTENT_URL = `${SECURE_BASE_URL}/billing/setup_intent`;
const STORE_SUBSCRIPTION_DETAILS = `${SECURE_BASE_URL}/billing/store_subscription_details`;
const VALIDATE_ZIP_URL = `${SECURE_BASE_URL}/billing/validate_zip`;
const ENABLE_RADAR_ZIP_CHECK = `${SECURE_BASE_URL}/billing/enable_radar_zip_check`;

export const SET_SHARED_BILLING_INFO = 'SET_SHARED_BILLING_INFO';
export const SET_IS_CHECKING_SUBSCRIPTION_CODE = 'SET_IS_CHECKING_SUBSCRIPTION_CODE';
export const SET_SUBSCRIPTION_CODE_ERROR_MESSAGE = 'SET_SUBSCRIPTION_CODE_ERROR_MESSAGE';
export const CLEAR_SUBSCRIPTION_CODE_INFO = 'CLEAR_SUBSCRIPTION_CODE_INFO';

const EXCEPTION_PREFIX = 'Oops';

export function setSharedBillingInfo(payload: Partial<SharedBillingState>) {
  return {
    type: SET_SHARED_BILLING_INFO,
    payload,
  };
}

export function setSubscriptionCodeValidationError(subscriptionCodeValidationError: string) {
  return {
    type: SET_SUBSCRIPTION_CODE_ERROR_MESSAGE,
    payload: {
      subscriptionCodeValidationError,
    },
  };
}

export function setIsCheckingSubscriptionCode(isCheckingSubscriptionCode: boolean) {
  return {
    type: SET_IS_CHECKING_SUBSCRIPTION_CODE,
    payload: {
      isCheckingSubscriptionCode,
    },
  };
}

export function clearSubscriptionCodeInfo() {
  return {
    type: CLEAR_SUBSCRIPTION_CODE_INFO,
    payload: {
      subscriptionCodeType: '',
      subscriptionCode: '',
      subscriptionCodeValidationError: '',
      subscriptionCodeDetails: [],
      subscriptionCodeValid: false,
      subscriptionCodeTermsUrl: '',
      isCheckingSubscriptionCode: false,
    },
  };
}

export function validateSubscriptionCode(
  subscriptionCode: SharedBillingState['subscriptionCode'],
): ThunkAction<Promise<void>, any, unknown, AnyAction> {
  return dispatch => {
    if (isNil(subscriptionCode)) {
      return Promise.resolve();
    }

    const validateSubscriptionCodeUrl = new Uri(VALIDATE_SUBSCRIPTION_CODE_URL)
      .addQueryParam('token', subscriptionCode)
      .toString();

    dispatch(setIsCheckingSubscriptionCode(true));

    return fetch(validateSubscriptionCodeUrl, {
      method: 'GET',
      credentials: 'include',
    })
      .then(checkResponse)
      .then(json => {
        dispatch(setIsCheckingSubscriptionCode(false));

        if (!json.success) {
          const defaultErrorMessage = `${EXCEPTION_PREFIX}! Failed to load information about your redemption code.`;
          const { errorMessage } = extractErrorMessage(json, defaultErrorMessage);
          dispatch(clearSubscriptionCodeInfo());
          dispatch(setSubscriptionCodeValidationError(errorMessage));
          return;
        }

        const { result = {} } = json;
        const { type, token, detailsList, errorMessage, terms_url: termsUrl } = result;

        if (errorMessage) {
          const message = `${errorMessage}, double check your entry.`;
          dispatch(clearSubscriptionCodeInfo());
          dispatch(setSubscriptionCodeValidationError(message));
          return;
        }

        dispatch(
          setSharedBillingInfo({
            subscriptionCodeValid: true,
            subscriptionCodeType: type,
            subscriptionCode: token,
            subscriptionCodeDetails: detailsList,
            subscriptionCodeValidationError: '',
            subscriptionCodeTermsUrl: termsUrl,
          }),
        );
      })
      .catch(error => {
        const errorMessage = `${EXCEPTION_PREFIX}! Caught an exception while loading code details: ${error.message}`;
        dispatch(clearSubscriptionCodeInfo());
        dispatch(setSubscriptionCodeValidationError(errorMessage));
      });
  };
}

export function flashSubscriptionCodeStatus(): ThunkAction<void, any, unknown, AnyAction> {
  return (dispatch, getState) => {
    const {
      sharedBilling: { subscriptionCodeValidationError },
    } = getState();

    if (subscriptionCodeValidationError) {
      const body = subscriptionCodeValidationError.startsWith(EXCEPTION_PREFIX)
        ? `${EXCEPTION_PREFIX}! Something went wrong. Try again.`
        : subscriptionCodeValidationError;
      dispatch(showFlashNotice({ body, type: NOTICE_ERROR_TYPE }));
      return;
    }

    dispatch(
      showFlashNotice({
        body: 'Coupon applied!',
        noticeDuration: SUBSCRIPTION_CODE_NOTICE_DURATION_MS,
      }),
    );
  };
}

export function setupStripeIntent(
  onSuccess: (result: SetupIntentResult) => void,
  onError: (error: SetupIntentError) => void,
  paymentsLimitedTo?: string,
) {
  const limitPaymentsParam = paymentsLimitedTo ? `?limit_to=${paymentsLimitedTo}` : '';

  return fetch(`${SETUP_STRIPE_INTENT_URL}${limitPaymentsParam}`, {
    method: 'POST',
    credentials: 'include',
  })
    .then(checkResponse)
    .then(json => {
      const { result, errors } = json;
      if (!isEmpty(errors)) {
        const defaultErrorMessage = `${EXCEPTION_PREFIX}! Something went wrong.`;
        const { errorMessage } = extractErrorMessage(json, defaultErrorMessage);
        onError({ ...errors[0], message: errorMessage });
      } else {
        onSuccess(result);
      }
    })
    .catch(error => {
      const errorMessage = `${EXCEPTION_PREFIX}, caught an exception while loading the payment form: ${error.message}`;
      onError({ ...error, message: errorMessage });
    });
}

export function setShouldSetupIntent(shouldSetupIntent: boolean) {
  return setSharedBillingInfo({
    shouldSetupIntent,
  });
}

export function resetStripePaymentInfo() {
  return setSharedBillingInfo({
    zipCode: '',
    stripePaymentMethodId: '',
    shouldSetupIntent: true,
  });
}

export function setStripePaymentErrorMessage(stripePaymentErrorMessage: string) {
  return setSharedBillingInfo({
    stripePaymentErrorMessage,
  });
}

export function setStripePaymentLoading(isLoading: boolean) {
  return setSharedBillingInfo({
    stripePaymentLoading: isLoading,
  });
}

export function setStripePaymentReady(isReady: boolean) {
  return setSharedBillingInfo({
    stripePaymentReady: isReady,
  });
}

export function setStripePaymentMethodId(stripePaymentMethodId: PaymentMethod | string) {
  return setSharedBillingInfo({
    stripePaymentMethodId,
  });
}

export function setSkipCustomZipCollection(skipCustomZipCollection: boolean) {
  return setSharedBillingInfo({
    skipCustomZipCollection,
  });
}

export function setUserZipCode(zipCode: string) {
  return setSharedBillingInfo({
    zipCode,
  });
}

export function setSelectedPackageId(selectedPackageId: PackageId) {
  return setSharedBillingInfo({
    selectedPackageId,
  });
}

export function setSelectedAddOnIds(selectedAddOnIds: Record<PackageId, boolean>) {
  return setSharedBillingInfo({
    selectedAddOnIds,
  });
}

export function storeSubscriptionDetails(
  isDirectSignup?: boolean,
  includePackageInfo?: boolean,
): ThunkAction<void, any, unknown, AnyAction> {
  return (dispatch, getState) => {
    const { sharedBilling } = getState();
    const {
      zipCode,
      subscriptionCode,
      subscriptionCodeType,
      setupIntentId,
      selectedPackageId,
      selectedAddOnIds,
    } = sharedBilling;

    fetch(STORE_SUBSCRIPTION_DETAILS, {
      method: 'POST',
      body: JSON.stringify({
        ...(isDirectSignup && {
          direct_signup: true,
        }),
        setupIntentId,
        zip: zipCode,
        ...(subscriptionCode &&
          subscriptionCodeType === OfferType.COUPON && {
            coupon: subscriptionCode,
          }),
        ...((isDirectSignup || includePackageInfo) && {
          subscription_plan: selectedPackageId,
          ...selectedAddOnIds,
        }),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    }).catch(error => {
      const errorMessage = `${EXCEPTION_PREFIX}, caught an exception while submitting your subscription: ${error.message}`;
      dispatch(setStripePaymentErrorMessage(errorMessage));
    });
  };
}

export function setAddOnPriceChangeConfig(payload: Partial<SharedBillingState>) {
  return setSharedBillingInfo({
    ...payload,
  });
}

export function zipValidation(
  paymentId?: string,
  zip?: string,
): ThunkAction<Promise<boolean>, any, unknown, AnyAction> {
  return (dispatch, getState) => {
    const { sharedBilling } = getState();
    const { zipCode } = sharedBilling;
    // SC NOTE:
    // `zipCode` is set to sharedBilling state when using the custom zip collection field (for payment types
    // that are not CARD or LINK).
    // `zip` as an argument will be passed-in explicitly in the case when CARD payment fails the Stripe auth,
    if (!zip && !zipCode) {
      const message = `${EXCEPTION_PREFIX}! ${GENERAL_ERROR_MSG}`;
      dispatch(setStripePaymentErrorMessage(message));
      return Promise.resolve(true);
    }

    return fetch(VALIDATE_ZIP_URL, {
      method: 'POST',
      body: JSON.stringify({
        zip: zip || zipCode,
        ...(!isNil(paymentId) && {
          payment_method: paymentId,
        }),
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then(checkResponse)
      .then(json => {
        const defaultErrorMessage = `${EXCEPTION_PREFIX}! Something went wrong.`;
        const { errorMessage, errorCode } = extractErrorMessage(json, defaultErrorMessage);
        let hasError = false;
        if (!json.success && errorCode !== ErrorCodes.ERROR_SURGE_ENCOUNTER) {
          dispatch(setStripePaymentErrorMessage(errorMessage));
          hasError = true;
        }
        return hasError;
      })
      .catch(error => {
        const errorMessage = `${EXCEPTION_PREFIX}, caught an exception while validating your zip code: ${error.message}`;
        dispatch(setStripePaymentErrorMessage(errorMessage));
        return true;
      });
  };
}

export function enableRadarZipCheck(): ThunkAction<Promise<boolean>, any, unknown, AnyAction> {
  return dispatch => {
    return fetch(ENABLE_RADAR_ZIP_CHECK, {
      method: 'POST',
      credentials: 'include',
    })
      .then(checkResponse)
      .then(json => {
        const defaultErrorMessage = `${EXCEPTION_PREFIX}! Something went wrong.`;
        const { errorMessage, errorCode } = extractErrorMessage(json, defaultErrorMessage);
        let hasError = false;
        if (!json.success && errorCode !== ErrorCodes.ERROR_SURGE_ENCOUNTER) {
          dispatch(setStripePaymentErrorMessage(errorMessage));
          hasError = true;
        }
        return hasError;
      })
      .catch(error => {
        const errorMessage = `${EXCEPTION_PREFIX}, caught an exception while while submitting your subscription: ${error.message}`;
        dispatch(setStripePaymentErrorMessage(errorMessage));
        return true;
      });
  };
}
